import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Award, Quote } from "lucide-react";
import { COMPANY } from "../lib/constants";
import { projects, getFeaturedProjects } from "../data/projects";
import { testimonials } from "../data/testimonials";
import ProjectCard from "../components/ProjectCard";

export default function Home() {
    const featured = getFeaturedProjects(3);
    const hasProjects = featured.length > 0;

    return (
        <div data-testid="home-page">
            {/* HERO — mat, mørk, rolig */}
            <section className="relative overflow-hidden bg-[#1A2A21] text-[#F9F6F0]">
                {/* Baggrundsbillede ved 25 % — diskret tekstur */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none opacity-[0.25]"
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_markus-toemrer/artifacts/vb8j2z87_645313550_122139776594995551_4967570623069322047_n.jpg"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
                {/* Mørkt overlay — gør baggrunden mat */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none bg-[#1A2A21]/40"
                />
                {/* Vignet — dybde uden støj */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 35% 55%, rgba(26,42,33,0) 0%, rgba(26,42,33,0.45) 75%, rgba(26,42,33,0.75) 100%)",
                    }}
                />
                {/* Blød fade til næste sektion */}
                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F9F6F0] via-[#F9F6F0]/70 to-transparent pointer-events-none"
                />

                <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32 md:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div className="overline mb-6 text-[#F9F6F0]/60">
                            Tømrerfirmaet Fyr &amp; Finér · Græsted
                        </div>
                        <h1 className="font-serif text-[2.8rem] leading-[1.02] sm:text-6xl lg:text-[5.2rem] text-[#F9F6F0] tracking-tight">
                            Detaljerne<br />
                            <span className="italic text-[#B08968]">
                                skaber
                            </span>
                            <br />helheden.
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-[#F9F6F0]/80 leading-relaxed font-light max-w-xl">
                            Kvalitetshåndværk uden kompromis. Hos Fyr &amp;
                            Finér tager vi både de små og de større opgaver —
                            fra et enkelt VELUX-vindue til en ny tagflade,
                            træterrasse eller tilbygning. Vi giver altid et
                            uforpligtende tilbud og kigger gerne forbi.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4 fade-up fade-up-delay-2">
                            <Link
                                to="/projekter"
                                data-testid="hero-cta-projects"
                                className="inline-flex items-center gap-2 bg-[#F9F6F0] hover:bg-[#EAE3D5] text-[#1A2A21] px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors rounded-sm"
                            >
                                Se projekter
                                <ArrowRight size={16} />
                            </Link>
                            <a
                                href={COMPANY.phoneHref}
                                data-testid="hero-phone-link"
                                className="inline-flex items-center gap-2 border border-[#F9F6F0]/50 hover:border-[#F9F6F0] text-[#F9F6F0] px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors rounded-sm"
                            >
                                <Phone size={14} /> {COMPANY.phone}
                            </a>
                        </div>

                        <div className="mt-14 flex flex-wrap gap-10 text-sm text-[#F9F6F0]/70 fade-up fade-up-delay-3">
                            <div className="flex items-start gap-3">
                                <Award
                                    size={18}
                                    className="text-[#B08968] mt-1"
                                />
                                <div>
                                    <div className="font-medium text-[#F9F6F0]">
                                        Sølvmedalje 2025
                                    </div>
                                    <div>
                                        Højeste udmærkelse i tømrerfaget
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin
                                    size={18}
                                    className="text-[#B08968] mt-1"
                                />
                                <div>
                                    <div className="font-medium text-[#F9F6F0]">
                                        Nordsjælland
                                    </div>
                                    <div>Græsted og omegn</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 fade-up fade-up-delay-1">
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 w-28 h-28 bg-[#F9F6F0]/10 rounded-sm hidden md:block" />
                            <div className="absolute -bottom-8 -right-6 w-40 h-40 bg-[#2F483A]/50 rounded-sm hidden md:block" />
                            <div className="relative img-hover aspect-[4/5] overflow-hidden rounded-sm ring-1 ring-[#F9F6F0]/20 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
                                <img
                                    src={COMPANY.portrait}
                                    alt="Markus Dræby Ley"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 left-6 md:-left-8 bg-[#F9F6F0] border border-[#D1C7B7] px-6 py-4 rounded-sm shadow-[0_20px_40px_-10px_rgba(0,0,0,0.5)]">
                                <div className="overline text-[0.62rem] mb-1 text-[#7A6855]">
                                    Sølvmedalje · 2025
                                </div>
                                <div className="font-serif text-xl text-[#2C241B]">
                                    Markus Dræby Ley
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* INTRO-bånd */}
            <section className="bg-[#EAE3D5]/60 border-y border-[#D1C7B7]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="overline mb-3">Det vi tager fat i</div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#2C241B]">
                            Solidt håndværk<br />og smarte løsninger.
                        </h2>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {[
                            {
                                t: "Tag, VELUX & undertag",
                                d: "Udskiftning af tagsten, nye ovenlysvinduer og tætte inddækninger. Når vi åbner et tag, lukker vi det igen samme dag.",
                            },
                            {
                                t: "Solceller — tag eller stativ",
                                d: "Solidt håndværk kombineret med grøn energi. Kan det ikke på taget, kan det i haven — vi er altid klar på nye løsninger.",
                            },
                            {
                                t: "Døre, vinduer & facade",
                                d: "Franske døre, kvalitetsvinduer og døre i mange stilarter — skræddersyet til huset og dine ønsker.",
                            },
                            {
                                t: "Terrasser, pergolaer & udeliv",
                                d: "Træterrasser, haveskure og pergolaer bygget i rigtigt træ — klar til at holde mange sæsoner.",
                            },
                        ].map((b) => (
                            <div
                                key={b.t}
                                className="pt-6 border-t border-[#2C241B]/15"
                                data-testid="service-block"
                            >
                                <div className="font-serif text-xl text-[#2C241B] mb-2">
                                    {b.t}
                                </div>
                                <p className="text-[#4A3B2C] text-sm leading-relaxed font-light">
                                    {b.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MANIFEST / CITAT */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center grain">
                <div className="overline mb-6">Sådan tænker vi</div>
                <blockquote className="font-serif text-4xl md:text-6xl leading-[1.1] text-[#2C241B]">
                    “Detaljerne skaber
                    <span className="italic text-[#2F483A]"> helheden</span>.”
                </blockquote>
                <p className="mt-8 text-base md:text-lg text-[#4A3B2C] font-light max-w-2xl mx-auto leading-relaxed">
                    Det er ikke noget, vi siger for at have det sagt. Det er
                    den målestok, vi lægger på hver eneste skrue, hver
                    inddækning og hver samling. Kvalitetshåndværk uden
                    kompromis — for alt andet er bare spån.
                </p>
            </section>

            {/* FEATURED PROJECTS */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                    <div>
                        <div className="overline mb-3">Seneste projekter</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C241B] max-w-xl">
                            Et udvalg fra værkstedet
                            <span className="italic text-[#8B5E3C]">.</span>
                        </h2>
                    </div>
                    <Link
                        to="/projekter"
                        data-testid="view-all-projects"
                        className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] font-medium text-[#2C241B] link-reveal"
                    >
                        Se alle projekter <ArrowRight size={16} />
                    </Link>
                </div>

                {hasProjects ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-14">
                        {featured.map((p, i) => (
                            <ProjectCard
                                key={p.slug}
                                project={p}
                                index={i}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="border border-dashed border-[#D1C7B7] rounded-sm p-12 text-center text-[#7A6855]">
                        Projekter tilføjes løbende.
                    </div>
                )}
            </section>

            {/* TESTIMONIALS */}
            {testimonials.length > 0 && (
                <section
                    data-testid="testimonials-section"
                    className="bg-[#EAE3D5]/60 border-y border-[#D1C7B7]"
                >
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                            <div>
                                <div className="overline mb-3">
                                    Hvad kunderne siger
                                </div>
                                <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C241B] max-w-2xl">
                                    De bedste
                                    <span className="italic text-[#8B5E3C]">
                                        {" "}anmeldelser{" "}
                                    </span>
                                    kommer fra det færdige arbejde.
                                </h2>
                            </div>
                            <a
                                href={COMPANY.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="testimonials-facebook-link"
                                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] font-medium text-[#2C241B] link-reveal"
                            >
                                Se på Facebook
                                <ArrowRight size={16} />
                            </a>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {testimonials.map((t, i) => (
                                <figure
                                    key={i}
                                    data-testid="testimonial-card"
                                    className="bg-[#F9F6F0] border border-[#D1C7B7] rounded-sm p-8 md:p-10 flex flex-col gap-6 transition-colors hover:border-[#2F483A]"
                                >
                                    <Quote
                                        size={24}
                                        className="text-[#8B5E3C] shrink-0"
                                        strokeWidth={1.5}
                                    />
                                    <blockquote className="font-serif text-xl md:text-2xl text-[#2C241B] leading-snug flex-1">
                                        {t.text}
                                    </blockquote>
                                    <figcaption className="flex items-center justify-between pt-4 border-t border-[#D1C7B7]">
                                        <div className="font-medium text-[#2C241B]">
                                            {t.name}
                                        </div>
                                        <div className="overline text-[0.62rem] text-[#7A6855]">
                                            {t.source}
                                        </div>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
                <div className="bg-[#2F483A] text-[#F9F6F0] rounded-sm px-8 md:px-16 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <div className="overline text-[#F9F6F0]/70 mb-4">
                            Har du et projekt?
                        </div>
                        <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                            Tag fat i os —<br />vi glæder os til at høre fra dig.
                        </h2>
                        <p className="mt-6 text-[#F9F6F0]/75 font-light max-w-xl">
                            Vi giver altid et uforpligtende tilbud. Vent ikke
                            for længe — kalenderen fyldes hurtigt op.
                        </p>
                    </div>
                    <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="cta-phone-link"
                            className="inline-flex items-center gap-2 bg-[#F9F6F0] text-[#2F483A] hover:bg-[#EAE3D5] px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors rounded-sm"
                        >
                            <Phone size={14} /> {COMPANY.phone}
                        </a>
                        <a
                            href={COMPANY.emailHref}
                            data-testid="cta-email-link"
                            className="text-sm text-[#F9F6F0]/80 link-reveal"
                        >
                            {COMPANY.email}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
